import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { first } from 'lodash';

import { useTranslation } from 'providers/GILT.provider';
import {
  selectCashup,
  selectCashupSelectedStaff,
  selectCashupSelectedStylistIds,
  selectCashupSoldVouchers,
} from 'selectors/cashup.selectors';
import { selectCheckoutTxLog } from 'selectors/checkout.selectors';
import { selectCurrentSalon, selectCurrentSalonClients, selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import {
  selectRsAbeCashupAllCombinedRead,
  selectRsAbeCashupOwnCombinedRead,
  selectRsBeGetCashup,
  selectRsBeGetReconcile,
  selectRsBeListReconcile,
} from 'selectors/request.selectors';
import asArray from 'utils/arrays/asArray.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import asObject from 'utils/objects/asObject.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';

import { getTransactionsByStylist } from 'cashup/actions';
import CashupLogs from 'cashup/CashupLogs';
import { clearTransactionLog, exportTransactionLog } from 'checkout/actions';

const CashupLoader = () => {
  const { t } = useTranslation();

  return (
    <div data-bem="CashupLoader" className="loading-wrap" style={{ position: 'fixed' }}>
      <div data-bem="CashupLoader__animation" className="loading-anim">
        <div data-bem="CashupLoader__title" className="loading-title">
          <p data-bem="CashupLoader__label">{t('cashUp.cashupLoader')}</p>
        </div>
      </div>
      <div data-bem="CashupLoader__feedback" className="loading-feedback">
        {t('cashUp.cashupLoaderMessage')}
      </div>
    </div>
  );
};

const CashupLogsWrapper = ({
  // from parent
  originals,
  onChangeOrder,
  onChangePage,
  onCheckout,
  onViewReceipt,
  onVoidTransaction,
  byRangeModal,
  // from mapDispatchToProps
  clearTransactionLog,
  exportTransactionLog,
  getTransactionsByStylist,
  // from mapStateToProps
  cashup: cashupFromProps,
  clients: clientsFromProps,
  rsBeGetCashup,
  rsBeGetReconcile,
  rsBeListReconcile,
  salonDetails,
  salonId,
  selectedFromDate,
  selectedToDate,
  selectedStaffGroup,
  selectedStylistIds,
  transactionLog,
  voucherSoldList,
  currentPage,
}) => {
  const { apiData, apiTransactionData, depositApiData, reconcileList } = asObject(cashupFromProps);
  const { isFetching: depositDataIsFetching, depositsLogs } = asObject(depositApiData);
  const { isFetching: apiDataIsFetching, count } = asObject(apiData);

  if (
    apiDataIsFetching ||
    !isFullArray(clientsFromProps) ||
    depositDataIsFetching ||
    rsBeGetCashup ||
    rsBeGetReconcile ||
    rsBeListReconcile
  ) {
    // NOTE: !(isFullArray(apiData.results) || isFullArray(depositsLogs)) were making the logs table hidden
    return <CashupLoader />;
  }

  const pageCount = Math.ceil(count / 100);

  const timeZone = salonDetails.timezone;

  const logs = asArray(depositsLogs);
  const clients = asArray(clientsFromProps);

  const depositData = {
    ...asObject(depositApiData),
    depositsLogs: logs.map((deposit) => ({
      ...first(clients.filter((client) => client && client.id === asObject(deposit).clientId)),
      ...deposit,
    })),
  };

  return (
    <CashupLogs
      cashUpTotals={apiData}
      depositData={depositData}
      transactions={originals}
      pageCount={pageCount}
      changePage={onChangePage}
      currentPage={currentPage}
      changeOrder={onChangeOrder}
      rsBeGetCashup={rsBeGetCashup}
      onViewReceipt={onViewReceipt}
      onVoidTransaction={onVoidTransaction}
      onCheckout={onCheckout}
      reconcileList={reconcileList}
      voucherSoldList={voucherSoldList}
      transactionLog={transactionLog}
      transactionLogSingleStylist={apiTransactionData}
      exportTransactionLog={exportTransactionLog}
      getTransactionsByStylist={getTransactionsByStylist}
      salonId={salonId}
      startDate={selectedFromDate}
      endDate={selectedToDate}
      timeZone={timeZone}
      byRangeModal={byRangeModal}
      selectedStaffGroup={selectedStaffGroup}
      selectedStylistIds={selectedStylistIds}
      clearTransactionLog={clearTransactionLog}
    />
  );
};

const CashupLogsWrapperHoc = withRouter(
  connect(
    (state) => ({
      cashup: selectCashup(state),
      clients: selectCurrentSalonClients(state),
      rsBeGetCashup:
        selectRsBeGetCashup(state) ||
        selectRsAbeCashupAllCombinedRead(state) ||
        selectRsAbeCashupOwnCombinedRead(state),
      rsBeGetReconcile: selectRsBeGetReconcile(state),
      rsBeListReconcile: selectRsBeListReconcile(state),
      salonId: selectCurrentSalonId(state),
      salonDetails: selectCurrentSalon(state),
      selectedStaff: selectCashupSelectedStaff(state),
      selectedStylistIds: selectCashupSelectedStylistIds(state),
      transactionLog: selectCheckoutTxLog(state),
      voucherSoldList: selectCashupSoldVouchers(state),
    }),
    mapActionsToDispatchToProps({
      clearTransactionLog,
      exportTransactionLog,
      getTransactionsByStylist,
    }),
  )(CashupLogsWrapper),
);

export default CashupLogsWrapperHoc;
